import type { ReceiptListItem, ReceiptListScope, ReceiptListType } from '@matix/types';
import { receiptOrderTypeName } from '@matix/types';
import type { XlsxSheet } from '@matix/utils';
import {
  ReportSheetBuilder,
  downloadReportExcel,
  type ExcelColumn,
  type ExcelRow,
} from '../reports/reportExcel';
import { formatReceiptDateOnly, formatReceiptTime } from './helpers';

/**
 * "Export to Excel" for the receipts archive.
 *
 * Deliberately the REPORTS' workbook, not a second spreadsheet style: the
 * same title block, the same pale-blue header band, the same banded totals
 * row, the same right-to-left sheet in an Arabic session — `ReportSheetBuilder`
 * and `downloadReportExcel` are imported rather than reimplemented, so a
 * change to the house style reaches this sheet too.
 *
 * What it holds is what the archive holds: the rows the CURRENT filters match
 * (every page of them, not the twenty on screen), in the table's own column
 * order. Money and the order number go in as NUMBERS so the column sums and
 * sorts; everything else is the same text the cashier is looking at.
 */

export interface ReceiptsExcelLabels {
  /** Column and badge text, already resolved for the active language. */
  title: string;
  orderNo: string;
  reference: string;
  type: string;
  orderType: string;
  customer: string;
  phone: string;
  payment: string;
  total: string;
  date: string;
  time: string;
  status: string;
  sale: string;
  return: string;
  credit: string;
  voided: string;
  posted: string;
  open: string;
  closedShift: string;
  scopeCurrentShift: string;
  scopeAll: string;
  /** Subtitle words for the Sales / Returns toggle; "All" adds nothing. */
  typeSales?: string;
  typeReturns?: string;
  totalRow: string;
  truncatedNote: string;
}

export interface ReceiptsExcelFilters {
  scope: ReceiptListScope;
  /** Absent = all. */
  type?: ReceiptListType;
  /** The chosen order type's NAME (already translated), '' / absent = every type. */
  orderTypeLabel?: string;
  /** The chosen payment type's NAME, '' / absent = every payment type. */
  paymentTypeLabel?: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
}

/** "2026-09-16 08:00 → 2026-09-16 14:30", or "all-time" when unbounded. */
export function receiptsRangeLabel(f: ReceiptsExcelFilters, allTime: string): string {
  const from = f.dateFrom ? `${f.dateFrom}${f.timeFrom ? ` ${f.timeFrom}` : ''}` : '';
  const to = f.dateTo ? `${f.dateTo}${f.timeTo ? ` ${f.timeTo}` : ''}` : '';
  if (!from && !to) return allTime;
  if (from && to) return `${from} → ${to}`;
  return from ? `${from} →` : `→ ${to}`;
}

function typeLabel(row: ReceiptListItem, l: ReceiptsExcelLabels): string {
  if (row.is_return) return l.return;
  if (row.is_credit) return l.credit;
  return l.sale;
}

function statusLabel(row: ReceiptListItem, l: ReceiptsExcelLabels): string {
  // Same precedence as the table: voided first and alone — once a receipt is
  // cancelled, whether its voucher posted stops being the thing to say.
  if (row.is_canceled) return l.voided;
  if (row.is_voucher_posted) return l.posted;
  if ((row.in_open_shift ?? row.in_current_shift)) return l.open;
  return l.closedShift;
}

/**
 * The sheet's net total.
 *
 * Voided receipts are excluded — they describe a sale that did not happen and
 * count for nothing in the shift, the Z report or any report, so a column that
 * silently included them would not reconcile with any of those. Returns
 * (Code 53) are stored POSITIVE, as the shift close reads them, so the net is
 * sales MINUS returns rather than a bare sum of the column.
 */
export function netTotalOf(rows: ReceiptListItem[]): { net: number; counted: number } {
  let net = 0;
  let counted = 0;
  for (const row of rows) {
    if (row.is_canceled) continue;
    counted += 1;
    net += row.is_return ? -row.net_value : row.net_value;
  }
  return { net, counted };
}

export interface ReceiptsExcelInput {
  rows: ReceiptListItem[];
  truncated: boolean;
  filters: ReceiptsExcelFilters;
  labels: ReceiptsExcelLabels;
  rangeLabel: string;
  rightToLeft: boolean;
}

/** The sheet itself — separate from the download so it can be asserted on. */
export function buildReceiptsSheet(opts: ReceiptsExcelInput): XlsxSheet {
  const { rows, labels: l, filters } = opts;

  const columns: ExcelColumn[] = [
    { label: l.orderNo, kind: 'int' },
    { label: l.reference, kind: 'text' },
    { label: l.type, kind: 'center' },
    { label: l.orderType, kind: 'center' },
    { label: l.customer, kind: 'text' },
    { label: l.phone, kind: 'text' },
    { label: l.payment, kind: 'text' },
    { label: l.total, kind: 'money' },
    { label: l.date, kind: 'center' },
    { label: l.time, kind: 'center' },
    { label: l.status, kind: 'center' },
  ];

  const body: ExcelRow[] = rows.map((row) => [
    row.entry_no,
    row.reference_no ?? '',
    typeLabel(row, l),
    receiptOrderTypeName(row.order_type, opts.rightToLeft ? 'ar' : 'en'),
    row.customer_name ?? '',
    row.customer_phone ?? '',
    row.payment_label,
    row.net_value,
    formatReceiptDateOnly(row.entry_date),
    formatReceiptTime(row.entry_date),
    statusLabel(row, l),
  ]);

  const { net, counted } = netTotalOf(rows);
  const scopeLabel = filters.scope === 'current_shift' ? l.scopeCurrentShift : l.scopeAll;
  const typeWord =
    filters.type === 'sales'
      ? l.typeSales ?? ''
      : filters.type === 'returns'
        ? l.typeReturns ?? ''
        : '';
  const subtitle = [scopeLabel, typeWord, filters.orderTypeLabel ?? '', filters.paymentTypeLabel ?? '', opts.rangeLabel, filters.search.trim()]
    .filter((s) => s.length > 0)
    .join(' · ');

  const sheet = new ReportSheetBuilder()
    .title(l.title, subtitle)
    .table(columns, body, {
      totals: [
        { v: `${l.totalRow} (${counted})`, kind: 'text' },
        null, null, null, null, null, null,
        net,
        null, null, null,
      ],
    })
    .build(l.title, { rightToLeft: opts.rightToLeft });

  if (opts.truncated) {
    // A note in the sheet, not only a toast: the file outlives the session it
    // was exported in, and whoever opens it later has to know it is partial.
    sheet.rows.push({ cells: [{ value: l.truncatedNote, style: 'note', span: columns.length }] });
  }

  return sheet;
}

/**
 * Build the workbook and hand it to the browser.
 *
 * The filename carries the window, so a cashier who exports twice in a shift
 * does not overwrite the first file.
 */
export function exportReceiptsExcel(opts: ReceiptsExcelInput): void {
  downloadReportExcel(`${opts.labels.title}_${opts.rangeLabel}`, buildReceiptsSheet(opts));
}
